import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Search,
  ScanBarcode,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  Banknote,
  Receipt,
  ShoppingCart,
  CheckCircle2,
  Keyboard,
  AlertCircle,
  Wallet,
  ArrowRight,
  Printer,
  X,
  Layers,
  Lock,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import SearchableSelect from '../components/SearchableSelect';
import ThermalReceipt from '../components/ThermalReceipt';
import CashierClosingModal from '../components/CashierClosingModal';

function generateInvoiceNumber() {
  const now = new Date();
  const date = now.toISOString().slice(0, 10).replace(/-/g, '');
  const rand = String(Math.floor(Math.random() * 9999) + 1).padStart(4, '0');
  return `INV-${date}-${rand}`;
}

export default function POS() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState('percent'); // 'percent' | 'fixed'
  const [taxRate, setTaxRate] = useState(0);
  const [paidAmount, setPaidAmount] = useState(0);
  const [customerId, setCustomerId] = useState(null);
  const [customerName, setCustomerName] = useState('Walk-in Customer');
  const [customers, setCustomers] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [settings, setSettings] = useState({});
  const [saleComplete, setSaleComplete] = useState(false);
  const [completedInvoice, setCompletedInvoice] = useState(null);
  const [printReceiptData, setPrintReceiptData] = useState(null);
  const [showPrintModal, setShowPrintModal] = useState(false);
  const [showClosingModal, setShowClosingModal] = useState(false);
  const searchRef = useRef(null);

  const [invoiceNumber, setInvoiceNumber] = useState(() => generateInvoiceNumber());

  useEffect(() => {
    loadData();
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F2') {
        e.preventDefault();
        searchRef.current?.focus();
      }
      if (e.key === 'F4') {
        e.preventDefault();
        completeSale();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cart, paymentMethod, paidAmount, discount, discountType, taxRate, customerId]);

  const loadData = async () => {
    try {
      const [custRes, settingsRes] = await Promise.allSettled([
        api.get('/customers?limit=500'),
        api.get('/settings'),
      ]);
      if (custRes.status === 'fulfilled') {
        const data = custRes.value;
        setCustomers(Array.isArray(data) ? data : (data?.customers || []));
      }
      if (settingsRes.status === 'fulfilled') {
        const data = settingsRes.value?.settings || settingsRes.value;
        if (data && typeof data === 'object') {
          setSettings(data);
        }
      }
    } catch (err) {
      toast.error(err.message || 'Failed to load POS data');
    }
  };

  useEffect(() => {
    const query = search.trim();
    if (!query) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await api.get(`/medicines?search=${encodeURIComponent(query)}&limit=50`);
        if (!cancelled) {
          setSearchResults(Array.isArray(response) ? response : (response?.medicines || []));
        }
      } catch (err) {
        if (!cancelled) {
          setSearchResults([]);
          toast.error(err.message || 'Failed to search medicines');
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [search]);

  const filtered = search
    ? searchResults
        .map((m) => ({
          ...m,
          stock: parseFloat(m.total_stock) || 0,
          price: parseFloat(m.selling_price) || 0,
        }))
    : [];

  const showResults = search.length > 0 && filtered.length > 0;

  const addToCart = async (medicine, preferredUnitName = null) => {
    try {
      const medData = await api.get(`/medicines/${medicine.id}`);
      const batches = (medData.batches || [])
        .filter((b) => b.quantity > 0 && (!b.expiry_date || new Date(b.expiry_date) >= new Date()))
        .sort((a, b) => {
          if (!a.expiry_date) return 1;
          if (!b.expiry_date) return -1;
          return new Date(a.expiry_date) - new Date(b.expiry_date);
        });
      const totalBaseStock = batches.reduce((sum, b) => sum + (parseFloat(b.quantity) || 0), 0);

      if (totalBaseStock <= 0) {
        toast.error(`No stock available for ${medicine.name}. Please add stock first.`);
        return;
      }

      const availableUnits = (medData.units && medData.units.length > 0)
        ? medData.units
        : [
            {
              unit_name: medicine.unit || 'pcs',
              level: 1,
              conversion_factor: 1,
              child_count: 1,
              cost_price: 0,
              selling_price: parseFloat(medicine.selling_price ?? medicine.price) || 0,
              is_base_unit: true,
            }
          ];

      const chosenUnit = preferredUnitName
        ? (availableUnits.find((u) => u.unit_name === preferredUnitName) || availableUnits[0])
        : availableUnits[0];

      const factor = chosenUnit.conversion_factor || 1;
      const unitPrice = parseFloat(chosenUnit.selling_price) || parseFloat(medicine.selling_price ?? medicine.price) || 0;
      const maxUnitStock = Math.floor(totalBaseStock / factor);

      if (maxUnitStock <= 0) {
        toast.error(`Insufficient stock to sell as 1 ${chosenUnit.unit_name}. Available: ${totalBaseStock} base units.`);
        return;
      }

      setCart((prev) => {
        const existingIndex = prev.findIndex((i) => i.medicine_id === medicine.id);
        if (existingIndex >= 0) {
          const existing = prev[existingIndex];
          if (existing.selected_unit === chosenUnit.unit_name) {
            if (existing.quantity >= maxUnitStock) {
              toast.error(`Max available stock reached (${maxUnitStock} ${chosenUnit.unit_name}s)`);
              return prev;
            }
            return prev.map((i, idx) =>
              idx === existingIndex ? { ...i, quantity: i.quantity + 1 } : i
            );
          }
        }
        return [
          ...prev,
          {
            medicine_id: medicine.id,
            name: medicine.name,
            generic_name: medicine.generic_name,
            base_unit: medicine.unit || 'units',
            units: availableUnits,
            selected_unit: chosenUnit.unit_name,
            conversion_factor: factor,
            unit_price: unitPrice,
            quantity: 1,
            total_base_stock: totalBaseStock,
            max_stock: maxUnitStock,
            batches,
          },
        ];
      });
      setSearch('');
      searchRef.current?.focus();
    } catch (err) {
      toast.error('Failed to add item: ' + err.message);
    }
  };

  const switchItemUnit = (medicineId, unitName) => {
    setCart((prev) =>
      prev.map((item) => {
        if (item.medicine_id === medicineId) {
          const newUnit = (item.units || []).find((u) => u.unit_name === unitName);
          if (!newUnit) return item;
          const newFactor = newUnit.conversion_factor || 1;
          const newMaxStock = Math.floor(item.total_base_stock / newFactor);

          if (newMaxStock <= 0) {
            toast.error(`Cannot switch to ${unitName}: insufficient stock for 1 ${unitName}. Available: ${item.total_base_stock} base units.`);
            return item;
          }

          const newQty = Math.min(Math.max(1, item.quantity), newMaxStock);
          return {
            ...item,
            selected_unit: newUnit.unit_name,
            conversion_factor: newFactor,
            unit_price: parseFloat(newUnit.selling_price) || 0,
            max_stock: newMaxStock,
            quantity: newQty,
          };
        }
        return item;
      })
    );
  };

  const updateQty = (medicineId, delta) => {
    setCart((prev) =>
      prev
        .map((i) =>
          i.medicine_id === medicineId
            ? { ...i, quantity: Math.max(0, Math.min(i.max_stock, (parseInt(i.quantity, 10) || 0) + delta)) }
            : i
        )
        .filter((i) => i.quantity > 0)
    );
  };

  const setDirectQty = (medicineId, qty) => {
    setCart((prev) =>
      prev.map((i) => {
        if (i.medicine_id === medicineId) {
          if (qty === '') return { ...i, quantity: '' };
          const num = parseInt(qty, 10);
          if (isNaN(num)) return i;
          if (num > i.max_stock) {
            toast.error(`Max available stock is ${i.max_stock} ${i.selected_unit}s`);
            return { ...i, quantity: i.max_stock };
          }
          return { ...i, quantity: Math.max(1, num) };
        }
        return i;
      })
    );
  };

  const removeFromCart = (medicineId) => {
    setCart((prev) => prev.filter((i) => i.medicine_id !== medicineId));
  };

  const selectCustomer = (cust) => {
    setCustomerId(cust ? cust.id : null);
    setCustomerName(cust ? cust.name : 'Walk-in Customer');
    if (!cust && paymentMethod === 'credit') {
      setPaymentMethod('cash');
    }
  };

  const selectedCustomer = customers.find((c) => String(c.id) === String(customerId)) || null;
  const customerBalance = parseFloat(selectedCustomer?.balance) || 0;
  const creditLimit = parseFloat(selectedCustomer?.credit_limit) || 0;

  const subtotal = cart.reduce((sum, i) => sum + (parseFloat(i.unit_price) || 0) * (parseFloat(i.quantity) || 0), 0);
  const discountAmount = discountType === 'percent'
    ? (subtotal * (parseFloat(discount) || 0)) / 100
    : (parseFloat(discount) || 0);
  const taxAmount = subtotal * ((parseFloat(taxRate) || 0) / 100);
  const grandTotal = Math.max(0, subtotal - discountAmount + taxAmount);
  const change = (parseFloat(paidAmount) || 0) - grandTotal;

  // Credit & Advance Ledger calculations
  const numericPaid = parseFloat(paidAmount) || 0;
  const netBillToCustomer = Math.max(0, grandTotal - numericPaid);
  const hasAdvance = customerBalance < 0;
  const advanceAvailable = hasAdvance ? Math.abs(customerBalance) : 0;
  const hasDues = customerBalance > 0;
  const duesAmount = hasDues ? customerBalance : 0;

  // How much is covered by advance deposit vs new credit dues
  const advanceDeducted = hasAdvance ? Math.min(advanceAvailable, netBillToCustomer) : 0;
  const creditDuesAdded = hasAdvance ? Math.max(0, netBillToCustomer - advanceAvailable) : netBillToCustomer;

  // Projected new balance after this sale
  const projectedBalance = customerBalance + netBillToCustomer - (numericPaid > grandTotal ? (numericPaid - grandTotal) : 0);
  const newHasAdvance = projectedBalance < 0;
  const newHasDues = projectedBalance > 0;

  const completeSale = async () => {
    if (cart.length === 0) return;

    if (paymentMethod === 'credit' && !customerId) {
      toast.error('Credit sales require selecting a registered customer.');
      return;
    }

    const total = Math.round(grandTotal * 100) / 100;
    const isDigital = paymentMethod === 'card' || paymentMethod === 'online';
    const paid = isDigital
      ? total
      : paymentMethod === 'credit'
      ? Math.round((parseFloat(paidAmount) || 0) * 100) / 100
      : Math.round((parseFloat(paidAmount) || total) * 100) / 100;
    const actualPaid = Math.min(paid, total);

    try {
      const apiItems = cart.map((item) => ({
        medicine_id: item.medicine_id,
        quantity: parseInt(item.quantity, 10) || 1,
        unit_name: item.selected_unit || 'pcs',
        conversion_factor: item.conversion_factor || 1,
        unit_price: item.unit_price,
        discount: 0,
      }));

      const res = await api.post('/pos/checkout', {
        customer_id: customerId,
        user_id: user?.id || 1,
        payment_method: paymentMethod,
        paid_amount: actualPaid,
        discount: discountAmount,
        tax_rate: parseFloat(taxRate) || 0,
        notes: null,
        items: apiItems,
      });

      const invNum = res?.invoice_number || invoiceNumber;
      const receiptSnapshot = {
        invoice: invNum,
        date: new Date(),
        items: [...cart],
        subtotal,
        discount: discountAmount,
        discountType,
        discountRate: parseFloat(discount) || 0,
        taxRate: parseFloat(taxRate) || 0,
        taxAmount,
        grandTotal: total,
        paidAmount: isDigital ? total : (parseFloat(paidAmount) || total),
        paymentMethod,
        change: paymentMethod === 'cash' ? Math.max(0, (parseFloat(paidAmount) || total) - total) : 0,
        customer: selectedCustomer,
        customerBalance,
        advanceDeducted,
        creditDuesAdded,
        projectedBalance,
        cashier: user?.full_name || user?.username || user?.name || 'Admin',
        cashier_role: user?.role || 'Admin',
        settings,
      };

      setPrintReceiptData(receiptSnapshot);
      setCompletedInvoice({
        invoice: invNum,
        total,
        items: cart.length,
        receiptData: receiptSnapshot,
      });
      setSaleComplete(true);
      setShowPrintModal(true);

      // Auto trigger print for thermal printer
      setTimeout(() => {
        window.print();
      }, 350);

      setCart([]);
      setDiscount(0);
      setPaidAmount(0);
      setTaxRate(0);
      setCustomerId(null);
      setCustomerName('Walk-in Customer');
      setPaymentMethod('cash');
      setSearch('');
      setInvoiceNumber(generateInvoiceNumber());

      loadData();
    } catch (err) {
      toast.error('Sale failed: ' + err.message);
    }
  };

  return (
    <div className="flex h-[calc(100vh-8rem)] gap-5">
      {/* ══════ LEFT: Search + Cart Items ══════ */}
      <div className="flex flex-1 flex-col gap-5 overflow-y-auto min-w-0">
        {/* Sale Complete Banner */}
        {saleComplete && completedInvoice && (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-center justify-between gap-3 shadow-xs">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-bold text-emerald-800">Sale Completed Successfully!</p>
                <p className="text-xs text-emerald-600">
                  Invoice: <span className="font-mono font-bold">{completedInvoice.invoice}</span> | Total: Rs. {(parseFloat(completedInvoice.total) || 0).toFixed(2)} | {completedInvoice.items} item(s)
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setPrintReceiptData(completedInvoice.receiptData);
                  setShowPrintModal(true);
                  setTimeout(() => window.print(), 100);
                }}
                className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white shadow-sm hover:bg-emerald-700 active:scale-95 transition-all"
              >
                <Printer className="h-4 w-4" />
                Print Receipt
              </button>
            </div>
          </div>
        )}

        {/* Find Medicine Card */}
        <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Search className="h-5 w-5 text-gray-600" />
              <h2 className="text-base font-semibold text-gray-900">Find Medicine</h2>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowClosingModal(true)}
                className="flex items-center gap-1.5 rounded-lg bg-indigo-50 border border-indigo-200 px-3 py-1 text-xs font-bold text-indigo-700 hover:bg-indigo-100 transition-colors"
              >
                <Lock className="h-3.5 w-3.5" />
                Close My Sales
              </button>
              <span className="rounded-md border border-gray-200 bg-gray-50 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                Multi-Unit POS
              </span>
            </div>
          </div>

          <div className="relative">
            <ScanBarcode className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-emerald-500" />
            <input
              ref={searchRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Scan barcode or type generic name..."
              className="w-full rounded-xl border-2 border-emerald-200 bg-gray-50 py-3.5 pl-12 pr-4 text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-emerald-500 focus:bg-white focus:outline-none focus:ring-0"
            />
          </div>

          {/* Search Results Dropdown */}
          {showResults && (
            <div className="mt-3 max-h-80 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg divide-y divide-gray-100">
              {filtered.map((med) => {
                const units = med.units || [];
                const topU = units[0];
                const isOutOfStock = med.stock <= 0;

                return (
                  <div
                    key={med.id}
                    className={clsx(
                      'p-3 transition-colors flex items-center justify-between gap-3',
                      isOutOfStock ? 'bg-gray-50 opacity-60' : 'hover:bg-emerald-50/50'
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-gray-900">{med.name}</p>
                      {med.generic_name && (
                        <p className="text-xs text-gray-400">{med.generic_name}</p>
                      )}
                      <p className={clsx('text-xs font-semibold mt-0.5', isOutOfStock ? 'text-red-500' : 'text-gray-500')}>
                        {isOutOfStock ? 'Out of stock' : `Stock: ${med.stock} ${med.unit || 'units'}`}
                      </p>
                    </div>

                    {/* Unit Quick Add Buttons */}
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {units.length > 0 ? (
                        units.map((u) => {
                          const uStock = Math.floor(med.stock / (u.conversion_factor || 1));
                          return (
                            <button
                              key={u.unit_name}
                              type="button"
                              onClick={() => addToCart(med, u.unit_name)}
                              disabled={uStock <= 0}
                              className={clsx(
                                'flex flex-col items-center rounded-lg border px-2.5 py-1 text-xs font-bold transition-all shadow-xs active:scale-95',
                                uStock > 0
                                  ? 'border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-500 hover:text-white'
                                  : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                              )}
                            >
                              <span>+ {u.unit_name}</span>
                              <span className="text-[9px] font-normal opacity-80">
                                Rs. {(parseFloat(u.selling_price) || 0).toFixed(2)}
                              </span>
                            </button>
                          );
                        })
                      ) : (
                        <button
                          type="button"
                          onClick={() => addToCart(med)}
                          disabled={isOutOfStock}
                          className="rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-600 disabled:opacity-50"
                        >
                          + Add (Rs. {(parseFloat(med.price) || 0).toFixed(2)})
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {search.length > 0 && !searchLoading && filtered.length === 0 && (
            <div className="mt-3 rounded-xl border border-gray-200 bg-white p-6 text-center shadow-lg">
              <p className="text-sm text-gray-500">No medicines found matching "{search}"</p>
              <p className="mt-1 text-xs text-gray-400">Add medicines from the Medicines page first</p>
            </div>
          )}
        </div>

        {/* Cart Items Card (Under Searchbar) */}
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm flex flex-col flex-1 min-h-[260px]">
          <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
            <div className="flex items-center gap-2.5">
              <ShoppingCart className="h-5 w-5 text-emerald-600" />
              <h2 className="text-base font-semibold text-gray-900">Cart Items</h2>
              <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-bold text-emerald-800">
                {cart.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0)} {cart.length === 1 ? 'item' : 'items'}
              </span>
            </div>
            {cart.length > 0 && (
              <button
                type="button"
                onClick={() => setCart([])}
                className="text-xs font-semibold text-red-500 hover:text-red-700 hover:underline transition-colors"
              >
                Clear Cart
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-100">
                  <ShoppingCart className="h-7 w-7 text-gray-300" />
                </div>
                <p className="text-sm font-semibold text-gray-500">Cart is empty</p>
                <p className="mt-1 text-xs text-gray-400">Scan a barcode or search medicines above to add items</p>
              </div>
            ) : (
              <div className="space-y-3">
                {cart.map((item) => {
                  const lineTotal = (parseFloat(item.unit_price) || 0) * (parseFloat(item.quantity) || 0);

                  return (
                    <div
                      key={item.medicine_id}
                      className="rounded-xl border border-gray-100 bg-gray-50/80 p-3.5 hover:bg-gray-50 transition-colors space-y-2.5"
                    >
                      {/* Top Row: Name, Unit Price, Line Total, Remove */}
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-bold text-gray-900">{item.name}</p>
                          <p className="text-xs text-emerald-700 font-semibold mt-0.5">
                            Rs. {(parseFloat(item.unit_price) || 0).toFixed(2)} per {item.selected_unit}
                          </p>
                        </div>

                        {/* Quantity Controls */}
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => updateQty(item.medicine_id, -1)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-100 shadow-xs active:scale-95"
                          >
                            <Minus className="h-3.5 w-3.5" />
                          </button>
                          <input
                            type="number"
                            min="1"
                            max={item.max_stock}
                            value={item.quantity}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => {
                              let val = e.target.value;
                              if (/^0[0-9]/.test(val)) {
                                val = val.replace(/^0+/, '');
                              }
                              setDirectQty(item.medicine_id, val);
                            }}
                            onBlur={(e) => {
                              const val = parseInt(e.target.value, 10);
                              if (isNaN(val) || val <= 0) {
                                setDirectQty(item.medicine_id, 1);
                              }
                            }}
                            className="w-14 rounded-lg border border-gray-200 bg-white py-1 text-center text-sm font-black text-gray-900 shadow-xs focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/20 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          />
                          <button
                            type="button"
                            onClick={() => updateQty(item.medicine_id, 1)}
                            disabled={item.quantity >= item.max_stock}
                            className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-40 shadow-xs active:scale-95"
                          >
                            <Plus className="h-3.5 w-3.5" />
                          </button>
                        </div>

                        {/* Line Total */}
                        <div className="text-right min-w-[90px]">
                          <span className="text-sm font-black text-gray-900">
                            Rs. {lineTotal.toFixed(2)}
                          </span>
                        </div>

                        {/* Remove button */}
                        <button
                          type="button"
                          onClick={() => removeFromCart(item.medicine_id)}
                          className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500"
                          title="Remove item"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Bottom Row: Dynamic Unit Switcher Pills */}
                      <div className="flex items-center justify-between gap-2 pt-1 border-t border-gray-200/50 flex-wrap">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                            <Layers className="h-3 w-3" /> Sell Unit:
                          </span>
                          {(item.units || []).map((u) => {
                            const isSelected = item.selected_unit === u.unit_name;
                            const uStock = Math.floor(item.total_base_stock / (u.conversion_factor || 1));
                            return (
                              <button
                                key={u.unit_name}
                                type="button"
                                onClick={() => switchItemUnit(item.medicine_id, u.unit_name)}
                                disabled={uStock <= 0}
                                className={clsx(
                                  'rounded-md px-2 py-0.5 text-xs font-bold transition-all flex items-center gap-1 shadow-2xs',
                                  isSelected
                                    ? 'bg-emerald-600 text-white shadow-emerald-600/20 ring-2 ring-emerald-600/30'
                                    : uStock <= 0
                                    ? 'bg-gray-100 text-gray-300 opacity-40 cursor-not-allowed'
                                    : 'bg-white border border-gray-200 text-gray-700 hover:border-emerald-400 hover:bg-emerald-50/50'
                                )}
                              >
                                <span>{u.unit_name}</span>
                                <span className={clsx('text-[10px] font-normal', isSelected ? 'text-emerald-100' : 'text-gray-400')}>
                                  (Rs. {(parseFloat(u.selling_price) || 0).toFixed(2)})
                                </span>
                              </button>
                            );
                          })}
                        </div>

                        {/* Stock breakdown caption */}
                        <div className="text-[10px] text-gray-500 font-medium">
                          Available: <strong className="text-gray-700">{item.max_stock} {item.selected_unit}s</strong> ({item.total_base_stock} {item.base_unit || 'base units'})
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Keyboard Shortcuts */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Keyboard className="h-4 w-4 text-gray-500" />
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500">
              Keyboard Shortcuts
            </h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
            {[
              { key: 'F2', label: 'FOCUS SEARCH' },
              { key: 'F4', label: 'CHECKOUT' },
              { key: 'Esc', label: 'CLOSE LISTS' },
              { key: 'Enter', label: 'PICK RESULT' },
            ].map((shortcut) => (
              <div
                key={shortcut.key}
                className="flex items-center gap-2.5 rounded-lg border border-gray-100 bg-gray-50 px-3 py-2"
              >
                <kbd className="rounded-md bg-white px-2 py-0.5 text-xs font-bold text-gray-700 shadow-sm border border-gray-200">
                  {shortcut.key}
                </kbd>
                <span className="text-xs font-medium text-gray-600 truncate">{shortcut.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════ RIGHT: Customer Selection & Billing / Checkout ══════ */}
      <div className="flex w-[400px] lg:w-[420px] flex-shrink-0 flex-col rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3.5 flex-shrink-0">
          <div className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-gray-600" />
            <span className="text-base font-semibold text-gray-900">Billing & Checkout</span>
          </div>
          <span className="rounded-md bg-gray-100 px-2.5 py-1 font-mono text-[10px] font-bold text-gray-500">
            {invoiceNumber}
          </span>
        </div>

        {/* Middle Scrollable Section: Customer, Payment Method, Credit Breakdown, Discount & Tax */}
        <div className="flex-1 overflow-y-auto px-5 py-3.5 space-y-3.5 min-h-0">
          {/* Customer Selector */}
          <div className="relative">
            <div className="flex items-center justify-between mb-1.5">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-gray-500">
                Customer
              </label>
              {selectedCustomer && (
                <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                  {selectedCustomer.phone || 'Registered'}
                </span>
              )}
            </div>
            <SearchableSelect
              value={customerId ? String(customerId) : ''}
              onChange={(val) => {
                const cust = customers.find((c) => String(c.id) === String(val)) || null;
                selectCustomer(cust);
              }}
              options={[
                { value: '', label: 'Walk-in Customer' },
                ...customers.map((c) => {
                  const bal = parseFloat(c.balance) || 0;
                  const balText = bal > 0 ? `• Due: Rs. ${bal.toFixed(2)}` : bal < 0 ? `• Adv: Rs. ${Math.abs(bal).toFixed(2)}` : '• Clear';
                  return {
                    value: String(c.id),
                    label: c.name,
                    sublabel: `${c.phone ? 'Ph: ' + c.phone + ' ' : ''}${balText}`,
                  };
                }),
              ]}
              placeholder="Walk-in Customer"
              searchPlaceholder="Search customer by name or phone..."
            />

            {selectedCustomer && (
              <div className="mt-2.5 rounded-lg border border-gray-100 bg-gray-50/90 p-2.5 text-xs space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-gray-500 font-medium">Customer Balance:</span>
                  <span
                    className={clsx(
                      'font-bold px-2 py-0.5 rounded text-[11px]',
                      customerBalance > 0
                        ? 'bg-amber-100 text-amber-800'
                        : customerBalance < 0
                        ? 'bg-emerald-100 text-emerald-800'
                        : 'bg-gray-200/70 text-gray-700'
                    )}
                  >
                    Rs. {Math.abs(customerBalance).toFixed(2)}{' '}
                    {customerBalance > 0 ? '(Owes)' : customerBalance < 0 ? '(Advance)' : '(Clear)'}
                  </span>
                </div>
                {creditLimit > 0 && (
                  <div className="flex items-center justify-between text-[11px] text-gray-400">
                    <span>Credit Limit:</span>
                    <span className="font-semibold text-gray-600">Rs. {creditLimit.toFixed(2)}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Payment Method Buttons */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { key: 'cash',   label: 'Cash',   icon: Banknote,   active: 'border-emerald-500 bg-emerald-50 text-emerald-700' },
              { key: 'card',   label: 'Card',   icon: CreditCard, active: 'border-blue-500 bg-blue-50 text-blue-700' },
              { key: 'credit', label: 'Credit', icon: Receipt,    active: 'border-amber-500 bg-amber-50 text-amber-700', disabled: !customerId },
            ].map((pm) => {
              const isDisabled = pm.disabled;
              return (
                <button
                  key={pm.key}
                  onClick={() => {
                    if (isDisabled) {
                      toast.error('Credit is only available for registered customers. Select a customer first.');
                      return;
                    }
                    setPaymentMethod(pm.key);
                    if (pm.key === 'credit' && (parseFloat(paidAmount) || 0) >= grandTotal) {
                      setPaidAmount(0);
                    } else if (pm.key !== 'credit' && (parseFloat(paidAmount) || 0) === 0 && grandTotal > 0) {
                      setPaidAmount(grandTotal);
                    }
                  }}
                  disabled={isDisabled}
                  title={isDisabled ? 'Credit sales require selecting a registered customer' : ''}
                  className={clsx(
                    'flex flex-col items-center justify-center gap-1 rounded-xl border-2 py-2 text-xs font-semibold transition-all',
                    isDisabled
                      ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400 opacity-60'
                      : paymentMethod === pm.key
                      ? pm.active
                      : 'border-gray-200 bg-white text-gray-500 hover:border-gray-300'
                  )}
                >
                  <pm.icon className="h-4 w-4" />
                  <span>{pm.label}</span>
                </button>
              );
            })}
          </div>

          {/* Credit Account Breakdown Card when Credit is selected */}
          {paymentMethod === 'credit' && selectedCustomer && (
            <div
              className={clsx(
                'rounded-2xl border p-3.5 text-xs space-y-3 shadow-xs transition-all',
                hasAdvance
                  ? 'border-emerald-200 bg-gradient-to-br from-emerald-50/95 via-emerald-50/50 to-teal-50/30'
                  : 'border-amber-200 bg-gradient-to-br from-amber-50/95 via-amber-50/50 to-orange-50/30'
              )}
            >
              <div className="flex items-center justify-between border-b pb-2 border-gray-200/60">
                <div className="flex items-center gap-2">
                  <div
                    className={clsx(
                      'flex h-6 w-6 items-center justify-center rounded-lg shadow-xs',
                      hasAdvance ? 'bg-emerald-600 text-white' : 'bg-amber-600 text-white'
                    )}
                  >
                    {hasAdvance ? <Wallet className="h-3.5 w-3.5" /> : <Receipt className="h-3.5 w-3.5" />}
                  </div>
                  <div>
                    <p
                      className={clsx(
                        'font-bold text-[11px] uppercase tracking-wider',
                        hasAdvance ? 'text-emerald-950' : 'text-amber-950'
                      )}
                    >
                      {hasAdvance ? 'Advance Credit Settlement' : 'Credit Account Dues'}
                    </p>
                    <p className="text-[10px] text-gray-500 font-medium">Customer: {selectedCustomer.name}</p>
                  </div>
                </div>

                <span
                  className={clsx(
                    'rounded-full px-2 py-0.5 text-[9px] font-bold shadow-xs',
                    hasAdvance
                      ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                      : hasDues
                      ? 'bg-amber-100 text-amber-800 border border-amber-200'
                      : 'bg-gray-100 text-gray-700 border border-gray-200'
                  )}
                >
                  {hasAdvance ? 'Advance Deposit' : hasDues ? 'Existing Dues' : 'Zero Balance'}
                </span>
              </div>

              <div className="space-y-1.5">
                <div className="flex justify-between items-center text-gray-600">
                  <span>{hasAdvance ? 'Current Advance Deposit:' : 'Current Outstanding Dues:'}</span>
                  <span className="font-bold text-gray-900">
                    Rs. {Math.abs(customerBalance).toFixed(2)}
                  </span>
                </div>

                <div className="flex justify-between items-center text-gray-700">
                  <span>Bill Total (Grand Total):</span>
                  <span className="font-extrabold text-gray-900">Rs. {grandTotal.toFixed(2)}</span>
                </div>

                {numericPaid > 0 && (
                  <div className="flex justify-between items-center text-emerald-700">
                    <span>Paid Upfront (Cash):</span>
                    <span className="font-bold">- Rs. {numericPaid.toFixed(2)}</span>
                  </div>
                )}

                {hasAdvance ? (
                  <>
                    <div className="flex justify-between items-center text-emerald-800 border-t border-emerald-200/60 pt-1.5 font-medium">
                      <span>Deducted from Advance:</span>
                      <span className="font-extrabold text-emerald-700">
                        - Rs. {advanceDeducted.toFixed(2)}
                      </span>
                    </div>

                    {creditDuesAdded > 0 && (
                      <div className="flex justify-between items-center text-amber-900 font-medium">
                        <span>Remaining Unpaid (New Dues):</span>
                        <span className="font-extrabold text-amber-800">
                          + Rs. {creditDuesAdded.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex justify-between items-center text-amber-900 border-t border-amber-200/60 pt-1.5 font-medium">
                    <span>Added to Credit Dues:</span>
                    <span className="font-extrabold text-amber-800">
                      + Rs. {creditDuesAdded.toFixed(2)}
                    </span>
                  </div>
                )}
              </div>

              <div className="rounded-xl bg-white/95 border border-gray-200/80 p-2.5 shadow-xs flex items-center justify-between">
                <div>
                  <p className="text-[9px] uppercase tracking-wider font-bold text-gray-400">
                    New Account Balance
                  </p>
                  <p
                    className={clsx(
                      'text-xs font-black mt-0.5',
                      newHasAdvance
                        ? 'text-emerald-700'
                        : newHasDues
                        ? 'text-amber-800'
                        : 'text-gray-700'
                    )}
                  >
                    Rs. {Math.abs(projectedBalance).toFixed(2)}{' '}
                    <span className="text-[10px] font-semibold">
                      {newHasAdvance
                        ? '(Remaining Advance)'
                        : newHasDues
                        ? '(Customer Total Dues)'
                        : '(Fully Settled)'}
                    </span>
                  </p>
                </div>
                <div
                  className={clsx(
                    'flex h-7 w-7 items-center justify-center rounded-full',
                    newHasAdvance
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-100 text-amber-700'
                  )}
                >
                  <ArrowRight className="h-3.5 w-3.5" />
                </div>
              </div>

              {creditLimit > 0 && projectedBalance > creditLimit ? (
                <div className="flex items-center gap-1.5 text-[10px] font-semibold text-red-600 bg-red-50 p-2 rounded-lg border border-red-200">
                  <AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <span>Exceeds credit limit (Limit: Rs. {creditLimit.toFixed(2)})</span>
                </div>
              ) : (
                <p
                  className={clsx(
                    'text-[10px] font-medium leading-relaxed rounded-lg p-2',
                    hasAdvance
                      ? 'bg-emerald-100/60 text-emerald-800'
                      : 'bg-amber-100/60 text-amber-900'
                  )}
                >
                  {hasAdvance && advanceAvailable >= netBillToCustomer
                    ? `✓ Rs. ${advanceDeducted.toFixed(2)} will be deducted directly from ${selectedCustomer.name}'s advance balance.`
                    : hasAdvance && advanceAvailable < netBillToCustomer
                    ? `⚠️ Advance of Rs. ${advanceAvailable.toFixed(2)} used. Leftover Rs. ${creditDuesAdded.toFixed(2)} added to customer dues.`
                    : `⚠️ Rs. ${creditDuesAdded.toFixed(2)} will be added to ${selectedCustomer.name}'s unpaid credit account.`}
                </p>
              )}
            </div>
          )}

          {/* Subtotal, Discount, Tax */}
          <div className="space-y-2 text-sm border-t border-gray-100 pt-3">
            <div className="flex justify-between text-gray-600">
              <span className="font-medium">SUB-TOTAL:</span>
              <span className="font-semibold">Rs. {subtotal.toFixed(2)}</span>
            </div>

            {/* Discount Section with % and Rs support */}
            <div className="space-y-1.5 rounded-xl border border-gray-100 bg-gray-50/60 p-2.5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-xs text-gray-700">DISCOUNT:</span>
                  {/* Type Selector: % vs Rs */}
                  <div className="inline-flex rounded-lg bg-gray-200/70 p-0.5 shadow-inner">
                    <button
                      type="button"
                      onClick={() => setDiscountType('percent')}
                      className={clsx(
                        'rounded-md px-2 py-0.5 text-[11px] font-bold transition-all',
                        discountType === 'percent'
                          ? 'bg-emerald-600 text-white shadow-xs'
                          : 'text-gray-600 hover:text-gray-900'
                      )}
                      title="Percentage Discount (%)"
                    >
                      %
                    </button>
                    <button
                      type="button"
                      onClick={() => setDiscountType('fixed')}
                      className={clsx(
                        'rounded-md px-2 py-0.5 text-[11px] font-bold transition-all',
                        discountType === 'fixed'
                          ? 'bg-emerald-600 text-white shadow-xs'
                          : 'text-gray-600 hover:text-gray-900'
                      )}
                      title="Rupees Discount (Rs.)"
                    >
                      Rs
                    </button>
                  </div>
                </div>

                <div className="flex items-center gap-1.5">
                  <div className="relative flex items-center">
                    <input
                      type="number"
                      min="0"
                      max={discountType === 'percent' ? 100 : undefined}
                      step={discountType === 'percent' ? "0.5" : "1"}
                      value={discount}
                      onFocus={(e) => e.target.select()}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (/^0[0-9]/.test(val)) {
                          val = val.replace(/^0+/, '');
                        }
                        if (val === '') {
                          setDiscount('');
                          return;
                        }
                        const num = parseFloat(val);
                        if (isNaN(num) || num < 0) {
                          setDiscount(0);
                        } else if (discountType === 'percent') {
                          setDiscount(Math.min(100, num));
                        } else {
                          setDiscount(num);
                        }
                      }}
                      onBlur={() => {
                        if (discount === '' || isNaN(parseFloat(discount))) {
                          setDiscount(0);
                        } else {
                          setDiscount(parseFloat(discount));
                        }
                      }}
                      placeholder="0"
                      className="w-24 rounded-lg border border-gray-200 bg-white px-2.5 py-1 text-right text-sm font-semibold text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/20 shadow-xs"
                    />
                    <span className="ml-1 text-xs font-bold text-gray-500">
                      {discountType === 'percent' ? '%' : 'Rs'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Presets & Calculated Discount Amount */}
              <div className="flex items-center justify-between pt-1 border-t border-gray-200/50">
                <div className="flex items-center gap-1 flex-wrap">
                  {discountType === 'percent'
                    ? [0, 5, 7, 10, 15].map((rate) => (
                        <button
                          key={rate}
                          type="button"
                          onClick={() => setDiscount(rate)}
                          className={clsx(
                            'rounded px-1.5 py-0.5 text-[10px] font-bold transition-all',
                            parseFloat(discount) === rate
                              ? 'bg-emerald-600 text-white shadow-xs'
                              : 'bg-white text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 border border-gray-200/70'
                          )}
                        >
                          {rate}%
                        </button>
                      ))
                    : [0, 10, 20, 50, 100].map((amt) => (
                        <button
                          key={amt}
                          type="button"
                          onClick={() => setDiscount(amt)}
                          className={clsx(
                            'rounded px-1.5 py-0.5 text-[10px] font-bold transition-all',
                            parseFloat(discount) === amt
                              ? 'bg-emerald-600 text-white shadow-xs'
                              : 'bg-white text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 border border-gray-200/70'
                          )}
                        >
                          Rs.{amt}
                        </button>
                      ))}
                </div>

                {discountAmount > 0 && (
                  <span className="text-[11px] font-extrabold text-emerald-700 bg-emerald-100/80 px-2 py-0.5 rounded-md border border-emerald-200">
                    - Rs. {discountAmount.toFixed(2)}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-600">TAX (%):</span>
              <input
                type="number"
                min="0"
                max="100"
                step="0.5"
                value={taxRate}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  let val = e.target.value;
                  if (/^0[0-9]/.test(val)) {
                    val = val.replace(/^0+/, '');
                  }
                  if (val === '') {
                    setTaxRate('');
                    return;
                  }
                  const num = parseFloat(val);
                  if (isNaN(num) || num < 0) {
                    setTaxRate(0);
                  } else if (num > 100) {
                    setTaxRate(100);
                  } else {
                    setTaxRate(val);
                  }
                }}
                onBlur={() => {
                  if (taxRate === '' || isNaN(parseFloat(taxRate))) {
                    setTaxRate(0);
                  } else {
                    setTaxRate(parseFloat(taxRate));
                  }
                }}
                placeholder="0"
                className="w-24 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1 text-right text-sm text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/20"
              />
            </div>
          </div>
        </div>

        {/* ══════ STICKY BOTTOM FOOTER: Grand Total, Paid Upfront, Due/Change & Complete Button ══════ */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50/80 px-5 py-3.5 space-y-2.5 shadow-[0_-4px_12px_rgba(0,0,0,0.03)]">
          <div className="flex justify-between items-center">
            <span className="text-base font-bold text-gray-900">NET TOTAL:</span>
            <span className="text-xl font-black text-emerald-600">Rs. {grandTotal.toFixed(2)}</span>
          </div>

          {paymentMethod === 'card' || paymentMethod === 'online' ? (
            <div className="flex items-center justify-between rounded-lg bg-blue-50/80 px-3 py-2 text-xs border border-blue-200">
              <span className="font-semibold text-blue-800">CHARGE TO {paymentMethod.toUpperCase()}:</span>
              <span className="font-black text-blue-700">Rs. {grandTotal.toFixed(2)}</span>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="font-medium text-xs text-gray-600">
                {paymentMethod === 'credit' ? 'PAID UPFRONT (CASH):' : 'CASH RECEIVED:'}
              </span>
              <input
                type="number"
                min="0"
                step="0.50"
                value={paidAmount}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  let val = e.target.value;
                  if (/^0[0-9]/.test(val)) {
                    val = val.replace(/^0+/, '');
                  }
                  if (val === '') {
                    setPaidAmount('');
                    return;
                  }
                  const num = parseFloat(val);
                  setPaidAmount(isNaN(num) || num < 0 ? 0 : num);
                }}
                onBlur={() => {
                  if (paidAmount === '' || isNaN(parseFloat(paidAmount))) {
                    setPaidAmount(0);
                  } else {
                    setPaidAmount(parseFloat(paidAmount));
                  }
                }}
                placeholder="0"
                className="w-24 rounded-lg border border-gray-200 bg-white px-3 py-1 text-right text-xs text-gray-900 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500/20 shadow-xs"
              />
            </div>
          )}

          {paymentMethod === 'credit' ? (
            hasAdvance && advanceAvailable >= netBillToCustomer ? (
              <div className="flex justify-between items-center text-emerald-800 bg-emerald-100/70 px-3 py-1.5 rounded-lg text-xs font-semibold border border-emerald-200">
                <div className="flex items-center gap-1.5">
                  <Wallet className="h-3.5 w-3.5 text-emerald-600" />
                  <span>DEDUCTED FROM ADVANCE:</span>
                </div>
                <span className="font-extrabold text-emerald-700">
                  Rs. {advanceDeducted.toFixed(2)}
                </span>
              </div>
            ) : hasAdvance && advanceAvailable < netBillToCustomer ? (
              <div className="space-y-1">
                <div className="flex justify-between items-center text-emerald-800 bg-emerald-100/70 px-2.5 py-1 rounded-md text-[11px] font-semibold border border-emerald-200">
                  <span>FROM ADVANCE:</span>
                  <span className="font-bold text-emerald-700">Rs. {advanceAvailable.toFixed(2)}</span>
                </div>
                <div className="flex justify-between items-center text-amber-900 bg-amber-100/70 px-2.5 py-1 rounded-md text-[11px] font-semibold border border-amber-200">
                  <span>REMAINING DUE (CREDIT):</span>
                  <span className="font-bold text-amber-900">
                    Rs. {creditDuesAdded.toFixed(2)}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-center text-amber-900 bg-amber-100/70 px-3 py-1.5 rounded-lg text-xs font-semibold border border-amber-200">
                <div className="flex items-center gap-1.5">
                  <Receipt className="h-3.5 w-3.5 text-amber-600" />
                  <span>ADDED TO DUES (CREDIT):</span>
                </div>
                <span className="font-extrabold text-amber-900">
                  Rs. {creditDuesAdded.toFixed(2)}
                </span>
              </div>
            )
          ) : paymentMethod === 'cash' ? (
            <div className="flex justify-between text-xs text-gray-600">
              <span className="font-medium">CASHBACK:</span>
              <span className={clsx('font-bold', change >= 0 ? 'text-gray-900' : 'text-red-500')}>
                Rs. {(parseFloat(change) || 0).toFixed(2)}
              </span>
            </div>
          ) : null}

          {/* Complete Sale Button */}
          <button
            onClick={completeSale}
            disabled={cart.length === 0}
            className={clsx(
              'flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold transition-all shadow-sm',
              cart.length > 0
                ? paymentMethod === 'credit'
                  ? hasAdvance && advanceAvailable >= netBillToCustomer
                    ? 'bg-emerald-600 text-white shadow-emerald-600/25 hover:bg-emerald-700 active:scale-[0.98]'
                    : 'bg-amber-600 text-white shadow-amber-600/25 hover:bg-amber-700 active:scale-[0.98]'
                  : 'bg-emerald-500 text-white shadow-emerald-500/25 hover:bg-emerald-600 active:scale-[0.98]'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            )}
          >
            <CheckCircle2 className="h-4 w-4" />
            {paymentMethod === 'credit'
              ? hasAdvance && advanceAvailable >= netBillToCustomer
                ? 'Complete Sale (Advance Balance)'
                : 'Complete Credit Sale'
              : 'Complete Sale'}
            <kbd className="rounded bg-white/20 px-1.5 py-0.5 text-[10px]">F4</kbd>
          </button>
        </div>
      </div>

      {/* ══════ THERMAL RECEIPT PRINT MODAL ══════ */}
      {showPrintModal && printReceiptData &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-xs"
              onClick={() => setShowPrintModal(false)}
            />
            <div className="relative max-h-[92vh] w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl flex flex-col z-10">
              {/* Modal Header */}
              <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4 bg-gray-50/80 flex-shrink-0">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                    <Printer className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900">Thermal Receipt</h3>
                    <p className="text-[10px] text-gray-500 font-mono font-bold">{printReceiptData.invoice}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPrintModal(false)}
                  className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-200/70 hover:text-gray-700 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Receipt Preview Scrollable Area */}
              <div className="flex-1 overflow-y-auto p-4 bg-gray-100 flex justify-center">
                <div className="w-[80mm] max-w-full bg-white shadow-md border border-gray-200 rounded-lg p-1.5">
                  <ThermalReceipt data={printReceiptData} />
                </div>
              </div>

              {/* Modal Footer Actions */}
              <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-white p-4 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowPrintModal(false)}
                  className="rounded-xl border border-gray-200 px-4 py-2.5 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Close & Next Sale
                </button>
                <button
                  type="button"
                  onClick={() => window.print()}
                  className="flex items-center gap-2 rounded-xl bg-emerald-600 px-5 py-2.5 text-xs font-bold text-white shadow-md shadow-emerald-600/25 hover:bg-emerald-700 active:scale-98 transition-all"
                >
                  <Printer className="h-4 w-4" />
                  Print Thermal Receipt
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* ══════ DEDICATED PRINT CONTAINER (MOUNTED AT BODY ROOT) ══════ */}
      {printReceiptData &&
        createPortal(
          <div id="printable-thermal-receipt" className="hidden print:block">
            <ThermalReceipt data={printReceiptData} />
          </div>,
          document.body
        )}

      {/* Cashier Daily Closing Modal */}
      <CashierClosingModal
        isOpen={showClosingModal}
        onClose={() => setShowClosingModal(false)}
      />
    </div>
  );
}
